import React, {useEffect, useMemo, useState} from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from "recharts";
import dashboardServices from "app/services/dashboard-services";
import {DASHBOARD_COLORS, metricChipSx, neutralChipSx} from "./chartColors";

const SELECT_ALL_VALUE = "__all__";

const QUICK_RANGES = [
    {label: "7D", days: 7},
    {label: "30D", days: 30},
    {label: "90D", days: 90}
];

const toDateInputValue = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const buildQuickRange = (days) => {
    const now = new Date();
    const start = new Date(now);
    start.setDate(now.getDate() - (days - 1));
    return {
        desde: toDateInputValue(start),
        hasta: toDateInputValue(now)
    };
};

const resolveDefaultGroupBy = (days) => {
    if (days > 60) return "week";
    return "day";
};

const formatQty = (value) => Number(value || 0).toLocaleString("es-CL", {maximumFractionDigits: 2});

const formatDateTime = (isoString) => {
    if (!isoString) return "-";
    return new Date(isoString).toLocaleString("es-CL", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    });
};

const formatPeriodLabel = (period, groupBy) => {
    const date = new Date(`${period}T00:00:00`);
    if (groupBy === "month") {
        return date.toLocaleDateString("es-CL", {month: "short", year: "2-digit"});
    }
    if (groupBy === "week") {
        const weekEnd = new Date(date);
        weekEnd.setDate(weekEnd.getDate() + 6);
        return `${date.toLocaleDateString("es-CL", {day: "2-digit", month: "short"})} - ${weekEnd.toLocaleDateString("es-CL", {day: "2-digit", month: "short"})}`;
    }
    return date.toLocaleDateString("es-CL", {day: "2-digit", month: "short"});
};

const CompletionTrendTooltip = ({active, payload, label}) => {
    if (!active || !payload?.length) return null;
    return (
        <Box
            sx={{
                p: 1.25,
                borderRadius: 1.5,
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "0 8px 24px rgba(0,0,0,0.08)"
            }}
        >
            <Typography variant="subtitle2" sx={{mb: 0.75}}>{label}</Typography>
            {payload.map((item) => (
                <Typography key={item.dataKey} variant="body2" sx={{color: item.color}}>
                    {item.name}: {Number(item.value || 0).toLocaleString("es-CL")}
                </Typography>
            ))}
            <Typography variant="body2" sx={{mt: 0.75, pt: 0.75, borderTop: "1px solid", borderColor: "divider", fontWeight: 600}}>Total: {payload.reduce((sum, item) => sum + Number(item.value || 0), 0).toLocaleString("es-CL")}</Typography>
        </Box>
    );
};

const detailHeaderCellSx = {
    px: 1,
    whiteSpace: "nowrap"
};

const detailCellSx = {
    px: 1,
    maxWidth: 160,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
};

const detailDateCellSx = {
    ...detailHeaderCellSx,
    minWidth: 124
};

const detailQtyCellSx = {
    ...detailHeaderCellSx,
    width: 92
};

const OrderCompletionTrend = () => {
    const [quickRange, setQuickRange] = useState(30);
    const [filters, setFilters] = useState(() => ({
        ...buildQuickRange(30),
        group_by: resolveDefaultGroupBy(30)
    }));
    const [drivers, setDrivers] = useState([]);
    const [selectedDriverIds, setSelectedDriverIds] = useState(null);
    const [series, setSeries] = useState([]);
    const [totals, setTotals] = useState({
        complete_orders: 0,
        partial_orders: 0,
        total_orders: 0
    });
    const [topDrivers, setTopDrivers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedDriver, setSelectedDriver] = useState(null);
    const [driverDetails, setDriverDetails] = useState([]);
    const [driverTotals, setDriverTotals] = useState({
        complete_orders: 0,
        partial_orders: 0,
        total_orders: 0
    });
    const [driverLoading, setDriverLoading] = useState(false);
    const [driverError, setDriverError] = useState("");
    const [modalStatusFilter, setModalStatusFilter] = useState(null);
    const [chartFilter, setChartFilter] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const [topDriversByPeriod, setTopDriversByPeriod] = useState([]);
    const [loadingPeriod, setLoadingPeriod] = useState(false);
    const [detailsAllMode, setDetailsAllMode] = useState(false);
    const [detailSearch, setDetailSearch] = useState("");

    const hasInvalidRange = Boolean(filters.desde && filters.hasta && filters.desde > filters.hasta);
    const allDriverIds = useMemo(() => drivers.map((driver) => driver.id), [drivers]);
    const allSelected = useMemo(() => {
        if (drivers.length === 0 || !Array.isArray(selectedDriverIds)) return false;
        return selectedDriverIds.length === drivers.length;
    }, [drivers.length, selectedDriverIds]);

    useEffect(() => {
        let isMounted = true;

        const loadData = async () => {
            if (!filters.desde || !filters.hasta || filters.desde > filters.hasta) {
                setLoading(false);
                return;
            }

            setLoading(true);
            setError("");
            try {
                const requestFilters = {...filters};
                if (Array.isArray(selectedDriverIds)) requestFilters.driver_ids = selectedDriverIds;
                const response = await dashboardServices.getOrderCompletionTrend(requestFilters);
                if (!isMounted) return;

                const responseDrivers = response?.drivers ?? [];
                setDrivers(responseDrivers);

                if (selectedDriverIds === null && responseDrivers.length > 0) {
                    setSelectedDriverIds(responseDrivers.map((driver) => driver.id));
                }

                setSeries(response?.series ?? []);
                setTotals(response?.totals ?? {complete_orders: 0, partial_orders: 0, total_orders: 0});
                setTopDrivers(response?.top_drivers ?? []);
            } catch (loadError) {
                if (!isMounted) return;
                setSeries([]);
                setTopDrivers([]);
                setTotals({complete_orders: 0, partial_orders: 0, total_orders: 0});
                setError("No fue posible cargar la tendencia de pedidos completos vs parciales.");
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        loadData();
        return () => {
            isMounted = false;
        };
    }, [filters, selectedDriverIds]);

    useEffect(() => {
        setSelectedPeriod(null);
        setTopDriversByPeriod([]);
    }, [filters, selectedDriverIds]);

    const chartData = useMemo(() => (
        series.map((row) => ({
            ...row,
            label: formatPeriodLabel(row.period, filters.group_by)
        }))
    ), [series, filters.group_by]);

    const driverRows = useMemo(() => (
        selectedPeriod ? topDriversByPeriod : topDrivers
    ), [selectedPeriod, topDriversByPeriod, topDrivers]);

    const driverRowsTotal = useMemo(() => (
        driverRows.reduce((acc, row) => {
            acc.complete += row.complete_orders ?? 0;
            acc.partial += row.partial_orders ?? 0;
            acc.total += row.total_orders ?? 0;
            return acc;
        }, {complete: 0, partial: 0, total: 0})
    ), [driverRows]);

    const filteredDriverDetails = useMemo(() => {
        const term = detailSearch.trim().toLowerCase();
        return driverDetails.filter((row) => {
            if (modalStatusFilter && row.completion_status !== modalStatusFilter) return false;
            if (!term) return true;
            return (
                (row.order_code || "").toLowerCase().includes(term) ||
                (row.customer_name || "").toLowerCase().includes(term)
            );
        });
    }, [driverDetails, modalStatusFilter, detailSearch]);

    const handleChartClick = async (data) => {
        if (!data?.activePayload?.length) return;
        const clickedPoint = data.activePayload[0]?.payload;
        if (!clickedPoint?.period) return;

        if (selectedPeriod?.period === clickedPoint.period) {
            setSelectedPeriod(null);
            setTopDriversByPeriod([]);
            return;
        }

        let desde = clickedPoint.period;
        let hasta = clickedPoint.period;
        if (filters.group_by === 'week') {
            const end = new Date(`${clickedPoint.period}T00:00:00`);
            end.setDate(end.getDate() + 6);
            hasta = toDateInputValue(end);
        } else if (filters.group_by === 'month') {
            const end = new Date(`${clickedPoint.period}T00:00:00`);
            end.setMonth(end.getMonth() + 1);
            end.setDate(0);
            hasta = toDateInputValue(end);
        }

        setSelectedPeriod({period: clickedPoint.period, label: clickedPoint.label, desde, hasta});
        setLoadingPeriod(true);
        setTopDriversByPeriod([]);

        try {
            const requestFilters = {desde, hasta, group_by: filters.group_by, top: 10};
            if (Array.isArray(selectedDriverIds)) requestFilters.driver_ids = selectedDriverIds;
            const response = await dashboardServices.getOrderCompletionTrend(requestFilters);
            setTopDriversByPeriod(response?.top_drivers ?? []);
        } catch (_e) {
            setTopDriversByPeriod([]);
        } finally {
            setLoadingPeriod(false);
        }
    };

    const handleQuickRange = (days) => {
        setQuickRange(days);
        const range = buildQuickRange(days);
        setFilters((prev) => ({
            ...prev,
            ...range,
            group_by: resolveDefaultGroupBy(days)
        }));
    };

    const handleDriversChange = (event) => {
        const {value} = event.target;
        const asArray = typeof value === "string" ? value.split(",") : value;

        if (asArray.includes(SELECT_ALL_VALUE)) {
            setSelectedDriverIds(allSelected ? [] : allDriverIds);
            return;
        }

        const parsedIds = asArray.map((id) => Number(id)).filter((id) => Number.isInteger(id) && id > 0);
        setSelectedDriverIds(parsedIds);
    };

    const openDriverModal = async (driver) => {
        const allMode = !driver;
        const detailRange = selectedPeriod
            ? {desde: selectedPeriod.desde, hasta: selectedPeriod.hasta}
            : {desde: filters.desde, hasta: filters.hasta};

        setSelectedDriver(driver || null);
        setDetailsAllMode(allMode);
        setDetailSearch("");
        setModalOpen(true);
        setDriverLoading(true);
        setDriverError("");
        setDriverDetails([]);
        setModalStatusFilter(null);
        setDriverTotals({complete_orders: 0, partial_orders: 0, total_orders: 0});

        try {
            const response = await dashboardServices.getDriverCompletionDetails({
                driver_id: driver?.driver_id,
                driver_ids: allMode && Array.isArray(selectedDriverIds) ? selectedDriverIds : undefined,
                desde: detailRange.desde,
                hasta: detailRange.hasta
            });
            setDriverDetails(response?.details ?? []);
            setDriverTotals(response?.totals ?? {complete_orders: 0, partial_orders: 0, total_orders: 0});
        } catch (modalError) {
            setDriverError("No fue posible cargar el detalle del conductor.");
        } finally {
            setDriverLoading(false);
        }
    };

    return (
        <React.Fragment>
            <Card sx={{borderRadius: 3, border: "1px solid", borderColor: "divider"}}>
                <CardContent sx={{p: {xs: 2, md: 3}}}>
                    <Stack spacing={2.5}>
                        <Stack spacing={0.5}>
                            <Typography variant="h4">Tendencia temporal: completos vs parciales</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Eje Y: cantidad de pedidos | Eje X: tiempo
                            </Typography>
                        </Stack>

                        <Stack direction={{xs: "column", lg: "row"}} spacing={1.5}>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                {QUICK_RANGES.map((option) => (
                                    <Chip
                                        key={option.days}
                                        label={option.label}
                                        clickable
                                        color={quickRange === option.days ? "primary" : "default"}
                                        variant={quickRange === option.days ? "filled" : "outlined"}
                                        onClick={() => handleQuickRange(option.days)}
                                    />
                                ))}
                            </Stack>

                            <TextField
                                size="small"
                                label="Desde"
                                type="date"
                                value={filters.desde}
                                onChange={(event) => setFilters((prev) => ({...prev, desde: event.target.value}))}
                                InputLabelProps={{shrink: true}}
                            />

                            <TextField
                                size="small"
                                label="Hasta"
                                type="date"
                                value={filters.hasta}
                                onChange={(event) => setFilters((prev) => ({...prev, hasta: event.target.value}))}
                                InputLabelProps={{shrink: true}}
                            />

                            <FormControl size="small" sx={{minWidth: 130}}>
                                <InputLabel>Agrupar por</InputLabel>
                                <Select
                                    label="Agrupar por"
                                    value={filters.group_by}
                                    onChange={(event) => setFilters((prev) => ({...prev, group_by: event.target.value}))}
                                >
                                    <MenuItem value="day">Día</MenuItem>
                                    <MenuItem value="week">Semana</MenuItem>
                                    <MenuItem value="month">Mes</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl size="small" sx={{minWidth: 260}}>
                                <InputLabel>Conductores</InputLabel>
                                <Select
                                    multiple
                                    value={selectedDriverIds ?? []}
                                    onChange={handleDriversChange}
                                    input={<OutlinedInput label="Conductores"/>}
                                    renderValue={(selected) => {
                                        if (drivers.length === 0) return "Sin conductores";
                                        if (selected.length === 0) return "Sin conductores seleccionados";
                                        if (selected.length === drivers.length) return "Todos los conductores";
                                        const namesById = drivers.reduce((acc, driver) => {
                                            acc[driver.id] = driver.name;
                                            return acc;
                                        }, {});
                                        return selected.map((id) => namesById[id]).filter(Boolean).join(", ");
                                    }}
                                >
                                    <MenuItem value={SELECT_ALL_VALUE}>
                                        <Checkbox checked={allSelected}/>
                                        <ListItemText primary="Seleccionar todos"/>
                                    </MenuItem>
                                    {drivers.map((driver) => (
                                        <MenuItem key={driver.id} value={driver.id}>
                                            <Checkbox checked={(selectedDriverIds ?? []).includes(driver.id)}/>
                                            <ListItemText primary={driver.name}/>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>

                        {hasInvalidRange && (
                            <Alert severity="warning">La fecha inicial no puede ser mayor que la fecha final.</Alert>
                        )}

                        {error && (
                            <Alert severity="error">{error}</Alert>
                        )}

                        <Stack direction="row" spacing={1} flexWrap="wrap">
                            <Chip
                                sx={metricChipSx(DASHBOARD_COLORS.complete, chartFilter === 'complete')}
                                label={`Completos: ${formatQty(totals.complete_orders)}`}
                                onClick={() => setChartFilter(chartFilter === 'complete' ? null : 'complete')}
                                clickable
                            />
                            <Chip
                                sx={metricChipSx(DASHBOARD_COLORS.partial, chartFilter === 'partial')}
                                label={`Parciales: ${formatQty(totals.partial_orders)}`}
                                onClick={() => setChartFilter(chartFilter === 'partial' ? null : 'partial')}
                                clickable
                            />
                            <Chip
                                sx={neutralChipSx(chartFilter === null && !selectedPeriod)}
                                label={`Total: ${formatQty(totals.total_orders)}`}
                                onClick={() => { setChartFilter(null); setSelectedPeriod(null); setTopDriversByPeriod([]); }}
                                clickable
                            />
                        </Stack>

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={8}>
                                <Box sx={{height: 390, bgcolor: "background.paper", borderRadius: 2, p: 1.5}}>
                                    {loading ? (
                                        <Stack alignItems="center" justifyContent="center" sx={{height: "100%"}}>
                                            <CircularProgress/>
                                        </Stack>
                                    ) : (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <LineChart data={chartData} margin={{top: 12, right: 16, left: 4, bottom: 8}} onClick={handleChartClick} style={{cursor: "pointer"}}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB"/>
                                                <XAxis dataKey="label" tick={{fontSize: 12}} interval="preserveStartEnd"/>
                                                <YAxis allowDecimals={false}/>
                                                <Tooltip content={<CompletionTrendTooltip/>}/>
                                                <Legend/>
                                                {selectedPeriod && (
                                                    <ReferenceLine x={selectedPeriod.label} stroke="#0891B2" strokeWidth={2} strokeDasharray="4 4"/>
                                                )}
                                                {(chartFilter === null || chartFilter === 'complete') && (
                                                    <Line
                                                        type="monotone"
                                                        dataKey="complete_orders"
                                                        name="Pedidos completos"
                                                        stroke={DASHBOARD_COLORS.complete}
                                                        strokeWidth={3}
                                                        dot={false}
                                                        activeDot={{r: 6}}
                                                    />
                                                )}
                                                {(chartFilter === null || chartFilter === 'partial') && (
                                                    <Line
                                                        type="monotone"
                                                        dataKey="partial_orders"
                                                        name="Pedidos parciales"
                                                        stroke={DASHBOARD_COLORS.partial}
                                                        strokeWidth={3}
                                                        dot={false}
                                                        activeDot={{r: 6}}
                                                    />
                                                )}
                                            </LineChart>
                                        </ResponsiveContainer>
                                    )}
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <Box sx={{height: 390, bgcolor: "background.paper", borderRadius: 2, p: 1.5}}>
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{mb: 1.5}}>
                                        <Typography variant="h6">Top conductores</Typography>
                                        {selectedPeriod && (
                                            <Chip
                                                size="small"
                                                label={selectedPeriod.label}
                                                onDelete={() => { setSelectedPeriod(null); setTopDriversByPeriod([]); }}
                                                sx={{bgcolor: "#0891B214", color: "#0891B2", border: "1px solid #0891B266", fontWeight: 600}}
                                            />
                                        )}
                                    </Stack>
                                    <TableContainer sx={{maxHeight: 320}}>
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Conductor</TableCell>
                                                    <TableCell align="right">Completos</TableCell>
                                                    <TableCell align="right">Parciales</TableCell>
                                                    <TableCell align="right">Total</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {(loading || loadingPeriod) ? (
                                                    <TableRow>
                                                        <TableCell colSpan={4} align="center">Cargando...</TableCell>
                                                    </TableRow>
                                                ) : driverRows.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={4} align="center">
                                                            Sin datos en el rango seleccionado
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    <React.Fragment>
                                                        {driverRows.map((row) => (
                                                            <TableRow
                                                                key={row.driver_id}
                                                                hover
                                                                onClick={() => openDriverModal(row)}
                                                                sx={{cursor: "pointer"}}
                                                            >
                                                                <TableCell>{row.driver_name}</TableCell>
                                                                <TableCell align="right">{formatQty(row.complete_orders)}</TableCell>
                                                                <TableCell align="right">{formatQty(row.partial_orders)}</TableCell>
                                                                <TableCell align="right">{formatQty(row.total_orders)}</TableCell>
                                                            </TableRow>
                                                        ))}
                                                        <TableRow
                                                            hover
                                                            onClick={() => openDriverModal(null)}
                                                            sx={{
                                                                cursor: "pointer",
                                                                "& td": {fontWeight: 700, borderTop: "2px solid", borderColor: "divider"}
                                                            }}
                                                        >
                                                            <TableCell>Total (ver todo)</TableCell>
                                                            <TableCell align="right">{formatQty(driverRowsTotal.complete)}</TableCell>
                                                            <TableCell align="right">{formatQty(driverRowsTotal.partial)}</TableCell>
                                                            <TableCell align="right">{formatQty(driverRowsTotal.total)}</TableCell>
                                                        </TableRow>
                                                    </React.Fragment>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            </Grid>
                        </Grid>
                    </Stack>
                </CardContent>
            </Card>

            <Dialog open={modalOpen} onClose={() => setModalOpen(false)} fullWidth maxWidth="lg">
                <DialogTitle>
                    {detailsAllMode
                        ? "Detalle de cumplimiento - Todos los conductores"
                        : (selectedDriver ? `Detalle de cumplimiento - ${selectedDriver.driver_name}` : "Detalle de cumplimiento")}
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                            <Chip
                                sx={metricChipSx(DASHBOARD_COLORS.complete, modalStatusFilter === 'complete')}
                                label={`Completos: ${formatQty(driverTotals.complete_orders)}`}
                                onClick={() => setModalStatusFilter(modalStatusFilter === 'complete' ? null : 'complete')}
                                clickable
                            />
                            <Chip
                                sx={metricChipSx(DASHBOARD_COLORS.partial, modalStatusFilter === 'partial')}
                                label={`Parciales: ${formatQty(driverTotals.partial_orders)}`}
                                onClick={() => setModalStatusFilter(modalStatusFilter === 'partial' ? null : 'partial')}
                                clickable
                            />
                            <Chip
                                sx={neutralChipSx(modalStatusFilter === null)}
                                label={`Total: ${formatQty(driverTotals.total_orders)}`}
                                onClick={() => setModalStatusFilter(null)}
                                clickable
                            />
                        </Stack>

                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Buscar por pedido o cliente"
                            value={detailSearch}
                            onChange={(event) => setDetailSearch(event.target.value)}
                        />

                        {driverError && <Alert severity="error">{driverError}</Alert>}

                        <TableContainer sx={{maxHeight: 460}}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={detailDateCellSx}>Fecha/Hora</TableCell>
                                        {detailsAllMode && <TableCell sx={detailHeaderCellSx}>Conductor</TableCell>}
                                        <TableCell sx={detailHeaderCellSx}>Pedido</TableCell>
                                        <TableCell sx={detailHeaderCellSx}>Cliente</TableCell>
                                        <TableCell sx={detailHeaderCellSx}>Estado</TableCell>
                                        <TableCell sx={detailQtyCellSx} align="right">Pedido</TableCell>
                                        <TableCell sx={detailQtyCellSx} align="right">Entregado</TableCell>
                                        <TableCell sx={detailHeaderCellSx}>Cumplimiento</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {driverLoading ? (
                                        <TableRow>
                                            <TableCell colSpan={detailsAllMode ? 8 : 7} align="center">Cargando detalle...</TableCell>
                                        </TableRow>
                                    ) : filteredDriverDetails.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={detailsAllMode ? 8 : 7} align="center">
                                                {detailSearch.trim() ? "Sin resultados para la búsqueda" : "Sin pedidos completos/parciales en el rango seleccionado"}
                                            </TableCell>
                                        </TableRow>
                                    ) : filteredDriverDetails.map((row) => (
                                        <TableRow key={row.order_id}>
                                            <TableCell sx={detailDateCellSx}>{formatDateTime(row.reference_at)}</TableCell>
                                            {detailsAllMode && <TableCell sx={{...detailCellSx, maxWidth: 140}} title={row.driver_name || ""}>{row.driver_name || "-"}</TableCell>}
                                            <TableCell sx={{...detailCellSx, maxWidth: 96}}>{row.order_code || "-"}</TableCell>
                                            <TableCell sx={{...detailCellSx, maxWidth: 180}} title={row.customer_name || ""}>{row.customer_name || "-"}</TableCell>
                                            <TableCell sx={{...detailCellSx, maxWidth: 104}}>{row.order_state_name || "-"}</TableCell>
                                            <TableCell sx={detailQtyCellSx} align="right">{formatQty(row.total_qty)}</TableCell>
                                            <TableCell sx={detailQtyCellSx} align="right">{formatQty(row.delivered_qty)}</TableCell>
                                            <TableCell sx={{...detailCellSx, maxWidth: 110}}>{row.completion_status_label || "-"}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setModalOpen(false)}>Cerrar</Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
};

export default OrderCompletionTrend;
